import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Search, 
  Filter, 
  ScanLine, 
  Upload, 
  Package,
  Eye,
  EyeOff,
  Grid3X3,
  List
} from "lucide-react";
import { ProductStatusCard } from "../ProductStatusCard";
import { useWorkstationStore } from "@/hooks/useWorkstationStore";
// import { VirtualList } from "@/components/ui/virtual-list"; // Will be implemented in Phase 2
import { cn } from "@/lib/utils";
import { ProductScannerPanel } from "../scanner/ProductScannerPanel";

export const IncomingScanView = () => {
  const {
    getFilteredProducts,
    selectedProducts,
    selectAllProducts,
    clearSelection,
    filters,
    setSearchQuery,
    setStatusFilter,
    setCategoryFilter,
    setPosFilter,
    getProductStats,
    categories
  } = useWorkstationStore();

  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);

  const filteredProducts = getFilteredProducts();
  const stats = getProductStats();

  // Virtual list configuration
  const itemHeight = viewMode === 'grid' ? 280 : 120;
  const itemsPerRow = viewMode === 'grid' ? 3 : 1;

  const handleSelectAll = () => {
    if (selectedProducts.size === filteredProducts.length) {
      clearSelection();
    } else {
      selectAllProducts(filteredProducts.map(p => p.id));
    }
  };

  const StatusFilterButton = ({ 
    status, 
    count, 
    label, 
    color 
  }: { 
    status: string; 
    count: number; 
    label: string; 
    color: string; 
  }) => (
    <Button
      variant={filters.statusFilter === status ? "default" : "outline"}
      size="sm"
      onClick={() => setStatusFilter(status as any)}
      className={cn(
        "flex items-center gap-1",
        filters.statusFilter === status && color
      )}
    >
      {label} ({count})
    </Button>
  );

  const handleProductScanned = (barcode: string) => {
    // Add scanned product logic here
    console.log('Product scanned:', barcode);
    // You can add product lookup or addition logic here
  };

  return (
    <div className="space-y-6">
      {/* Header with Search and Actions */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search products by name, barcode, or enhanced name..."
            value={filters.searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <div className="flex items-center gap-2">
          {/* View Mode Toggle */}
          <div className="flex rounded-lg border">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grid')}
              className="rounded-r-none"
            >
              <Grid3X3 className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="rounded-l-none"
            >
              <List className="w-4 h-4" />
            </Button>
          </div>

          {/* Filter Toggle */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2"
          >
            <Filter className="w-4 h-4" />
            Filters
            {showFilters ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
          </Button>

          {/* Scanner Integration */}
          <Button 
            variant="outline" 
            size="sm" 
            className="flex items-center gap-2"
            onClick={() => setScannerOpen(true)}
          >
            <ScanLine className="w-4 h-4" />
            Scan
          </Button>

          {/* Batch Upload */}
          <Button variant="outline" size="sm" className="flex items-center gap-2">
            <Upload className="w-4 h-4" />
            Photos
          </Button>
        </div>
      </div>


      {/* Filters Panel */}
      {showFilters && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Filters</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Status Filters */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Status</label>
              <div className="flex flex-wrap gap-2">
                <StatusFilterButton
                  status="all"
                  count={stats.total}
                  label="All"
                  color="bg-muted text-muted-foreground"
                />
                <StatusFilterButton
                  status="ready"
                  count={stats.ready}
                  label="Ready"
                  color="bg-success/10 text-success"
                />
                <StatusFilterButton
                  status="needs-fix"
                  count={stats.needsFix}
                  label="Needs Fix"
                  color="bg-warning/10 text-warning"
                />
                <StatusFilterButton
                  status="blocked"
                  count={stats.blocked}
                  label="Blocked"
                  color="bg-destructive/10 text-destructive"
                />
              </div>
            </div>

            {/* POS Status Filter */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">POS Status</label>
              <div className="flex gap-2">
                <Button
                  variant={filters.posFilter === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPosFilter('all')}
                >
                  All ({stats.total})
                </Button>
                <Button
                  variant={filters.posFilter === 'new' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPosFilter('new')}
                >
                  New ({stats.total - stats.ready})
                </Button>
                <Button
                  variant={filters.posFilter === 'existing' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPosFilter('existing')}
                >
                  Existing ({stats.ready})
                </Button>
              </div>
            </div>

            {/* Category Filter */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Category</label>
              <select
                value={filters.categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm"
              >
                <option value="all">All Categories</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Selection Bar */}
      {filteredProducts.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={selectedProducts.size === filteredProducts.length && filteredProducts.length > 0}
                  onCheckedChange={handleSelectAll}
                  className="mr-2"
                />
                <span className="text-sm">
                  {selectedProducts.size > 0 
                    ? `${selectedProducts.size} selected` 
                    : `Select all ${filteredProducts.length} products`
                  }
                </span>
              </div>

              {selectedProducts.size > 0 && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm">
                    Bulk Edit
                  </Button>
                  <Button variant="outline" size="sm">
                    Assign Category
                  </Button>
                  <Button variant="outline" size="sm" onClick={clearSelection}>
                    Clear Selection
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Products Display */}
      {filteredProducts.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Package className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No products found</h3>
            <p className="text-gray-600">
              {filters.searchQuery 
                ? "Try adjusting your search or filters" 
                : "No products match the current filters"
              }
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className={cn(
          "gap-6",
          viewMode === 'grid' ? "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3" : "space-y-3"
        )}>
          {filteredProducts.map((product) => (
            <ProductStatusCard
              key={product.id}
              product={product}
              isSelected={selectedProducts.has(product.id)}
              showSelection
              variant={viewMode === 'grid' ? 'detailed' : 'compact'}
            />
          ))}
        </div>
      )}

      {/* Enhanced Scanner Integration */}
      <ProductScannerPanel
        isOpen={scannerOpen}
        onProductScanned={handleProductScanned}
        onClose={() => setScannerOpen(false)}
      />
    </div>
  );
};